import React, { useEffect, useState, useRef } from 'react';
import { Box, Tabs, Tab, Typography, styled } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { convertZonetoUTCEnd, convertZonetoUTCStart,convertZonetoUTC,getDatePickerDate } from '../../../../Helper/DatePicker/DateConverter';
import { PrimaryTextButton } from '../TableStyles';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from '../../../../Helper/Common/common_helpers';
import MultiSelectFilter from './filterTypes/MultiSelectFilter';
import DateCalenderFilter from './filterTypes/DateCalenderFilter';
import RangeFilter from './filterTypes/RangeFilter';
import AntDateAndTimePicker from '../../../../Helper/DatePicker/AntDateAndTimePicker';
import CompositeSelectFilter, { isCompositeFilterValid } from './filterTypes/CompositeSelectFilter';
const StyledFilterTab = styled((props) => <Tab disableRipple {...props} />)(
	({ theme, isFilterApplied }) => ({
		textTransform: "none",
		minWidth: 0,
		minHeight: "10px",
		width: "100%",
		textAlign: "left",
		fontFamily: "Roboto",
		fontSize: "12px",
		fontWeight: 500,
		padding: "4px 16px 4px 8px",
		borderRadius: "8px",
		transition: "background 0.2s ease-in-out, color 0.2s ease-in-out",
		alignItems: "start",
		marginBottom: "2px",
		color: "#3D4B5A",
		position: "relative",

		"&:hover": {
			background: "#F7F7F7",
			color: "#3D4B5A",
		},
		"&.Mui-selected": {
			background: "#EEEFFF",
			color: "#6D72F6",
			fontWeight: "bold",
		},

		// Custom left highlighter
		...(isFilterApplied && {
			"&::before": {
				content: '""',
				position: "absolute",
				left: 0,
				top: "25%",
				height: "50%",
				width: "2px",
				backgroundColor: "#6D72F6",
				borderRadius: "2px",
			},
		}),
	})
);


const FilterBody = ({ filters = {}, newFilters, setNewFilters, activeFilterKey = null, calendarConfigs = {} }) => {
	const [selectedTab, setSelectedTab] = useState(0);
	const [filterOptions, setFilterOptions] = useState([]);
	const { t } = useTranslation(['common', 'generic_table', 'filters']);
	const prevSelectedTabRef = useRef(null);

	const handleDateChangeFilter = (filterKey, filterValue) => {
		if (!filterValue || !filterValue.startDate || !filterValue.endDate) {
			return false
		}
		filterValue.startDateUTC = convertZonetoUTCStart(filterValue.startDate)
		filterValue.endDateUTC = convertZonetoUTCEnd(filterValue.endDate)


		setNewFilters((prevFilters) => {
			const updatedFilters = { ...prevFilters };
			updatedFilters[filterKey] = [JSON.stringify(filterValue)];
			return updatedFilters;
		})
	}


	const handleDateTimeChangeFilter = (filterKey, filterValue) => {
		if (!filterValue?.startDate || !filterValue?.endDate) {
			setNewFilters((prev) => {
				const updated = { ...prev };
				delete updated[filterKey];
				return updated;
			});
			return;
		}

		const payload = {
			...filterValue,
			startDateUTC: convertZonetoUTC(
				filterValue.startDate,
				"YYYY-MM-DD HH:mm:ss"
			),
			endDateUTC: convertZonetoUTC(
				filterValue.endDate,
				"YYYY-MM-DD HH:mm:ss"
			),
		};

		setNewFilters((prev) => ({
			...prev,
			[filterKey]: [JSON.stringify(payload)],
		}));
	};

	const getDateTimePickerValue = (filterKey) => {
		const current = newFilters?.[filterKey]?.[0];
		if (!current) return null;

		try {
			const parsed = JSON.parse(current);
			if (!parsed?.startDate || !parsed?.endDate) return null;
			return [parsed.startDate, parsed.endDate];
		} catch {
			return null;
		}
	};


	const handleRangeFilterChange = (filterKey, filterValue) => {
		console.log('range filter change', filterKey, filterValue)
		setNewFilters((prevFilters) => {
			const updatedFilters = { ...prevFilters };
			updatedFilters[filterKey] = [JSON.stringify({ ...filterValue, start: filterValue.min, end: filterValue.max, })];
			return updatedFilters;
		})
	}

	const handleCheckboxChange = (filterKey, filterValue) => (event) => {
		setNewFilters((prevFilters) => {
			const updatedFilters = { ...prevFilters };
			if (event.target.checked) {
				if (!updatedFilters[filterKey]) {
					updatedFilters[filterKey] = [];
				}
				updatedFilters[filterKey].push(filterValue);
			} else {

				if (updatedFilters[filterKey]) {
					updatedFilters[filterKey] = updatedFilters[filterKey].filter(
						(value) => value !== filterValue
					);

					if (updatedFilters[filterKey].length === 0) {
						delete updatedFilters[filterKey];
					}
				}
			}
			return updatedFilters;
		});
	};

	const handleRadioChange = (filterKey, filterValue) => {
		setNewFilters((prevFilters) => {
			const updatedFilters = { ...prevFilters };
			// Store only rangeValue as JSON string for backend calculations
			updatedFilters[filterKey] = [JSON.stringify(filterValue.rangeValue || filterValue)];
			return updatedFilters;
		});
	};

	const clearTabFilterSelection = () => {
		const currentFilter = filterOptions[selectedTab];
		const updatedNewFilters = { ...newFilters };

		// Handle composite filters differently - clear all subfilter keys
		if (currentFilter?.displayType === 'compositeSelectFilter') {
			currentFilter.subFilters?.forEach((subFilter) => {
				delete updatedNewFilters[subFilter.key];
			});
		} else {
			// For regular filters, just delete the main key
			delete updatedNewFilters[currentFilter?.key];
		}

		setNewFilters(updatedNewFilters);
	}

	const shouldShowClearSelection = () => {
		const currentFilter = filterOptions[selectedTab];

		// Handle composite filters - check if any subfilter has selections
		if (currentFilter?.displayType === 'compositeSelectFilter') {
			return currentFilter.subFilters?.some((subFilter) => {
				return newFilters && newFilters[subFilter.key] && newFilters[subFilter.key].length > 0;
			});
		}

		// For regular filters, check the main key
		return newFilters && newFilters[currentFilter?.key];
	}

	const setTheTabToCurrentFilterKey = () => {
		if (activeFilterKey && filterOptions.length > 0) {
			const index = filterOptions.findIndex(filter => filter.key === activeFilterKey);
			if (index !== -1) {
				setSelectedTab(index);
			}
		}
	}

	useEffect(() => {
		if (filters && Object.keys(filters).length > 0) {
			setFilterOptions(Object.values(filters));
		}
	}, [filters]);

	useEffect(() => {
		setTheTabToCurrentFilterKey()
	}, [activeFilterKey, filterOptions]);

	// Auto-clear partially selected composite filters when switching away from composite filter
	useEffect(() => {
		// Only run when selectedTab actually changes
		if (prevSelectedTabRef.current === selectedTab) return;

		const prevTabIndex = prevSelectedTabRef.current;
		prevSelectedTabRef.current = selectedTab;

		// Skip on initial render
		if (prevTabIndex === null) return;

		// Find the composite filter
		const compositeFilter = filterOptions.find(
			filter => filter?.displayType === 'compositeSelectFilter'
		);

		if (!compositeFilter) return;

		// Get the index of the composite filter
		const compositeFilterIndex = filterOptions.findIndex(
			filter => filter?.displayType === 'compositeSelectFilter'
		);

		// Check if user is switching away FROM composite filter TO another filter
		const wasPreviouslyOnCompositeFilter = prevTabIndex === compositeFilterIndex;
		const isNowOnDifferentFilter = selectedTab !== compositeFilterIndex;

		if (wasPreviouslyOnCompositeFilter && isNowOnDifferentFilter) {
			// User switched from composite filter to another filter
			// Check if composite filter has any selections
			const hasAnySubFilterSelected = compositeFilter.subFilters?.some((subFilter) => {
				return newFilters[subFilter.key] && newFilters[subFilter.key].length > 0;
			});

			if (hasAnySubFilterSelected) {
				// Check if composite filter is valid (both checkbox and radio selected)
				const isValid = isCompositeFilterValid(compositeFilter, newFilters);

				// If invalid (partial selection), clear all composite filter selections
				if (!isValid) {
					const clearedFilters = { ...newFilters };
					compositeFilter.subFilters?.forEach((subFilter) => {
						delete clearedFilters[subFilter.key];
					});
					setNewFilters(clearedFilters);
				}
			}
		}
	}, [selectedTab, filterOptions, newFilters, setNewFilters]);


	const renderFilterSelectionView = (filter) => {
		if (!filter) {
			return null
		}

		if (filter?.displayType == 'multiSelectDropdown') {
			return (
				<MultiSelectFilter
					key={filter.key}
					filter={filter}
					newFilters={newFilters}
					onChange={handleCheckboxChange}
				/>
			)
		}

		if (filter?.displayType == 'dateCalender') {
			return (
				<DateCalenderFilter
					key={filter.key}
					filter={filter}
					newFilters={newFilters}
					onChange={handleDateChangeFilter}
					{...(filter.key && calendarConfigs?.[filter.key] ? calendarConfigs[filter.key] : {})}
				/>
			)
		}
		if (filter?.displayType === 'dateTimeCalender') {
			return (
				<AntDateAndTimePicker
					defaultValue={getDateTimePickerValue(filter.key)}
					handleOnChange={(value) =>
						handleDateTimeChangeFilter(filter.key, value)
					}
				/>
			);
		}



		if (filter?.displayType == 'range') {
			return (
				<RangeFilter
					key={filter.key}
					filter={filter}
					newFilters={newFilters}
					onChange={handleRangeFilterChange}
				/>
			)
		}

		if (filter?.displayType === 'compositeSelectFilter') {
			return (
				<CompositeSelectFilter
					key={filter.key}
					filter={filter}
					newFilters={newFilters}
					onChange={handleCheckboxChange}
					onRadioChange={handleRadioChange}
				/>
			);
		}

		return <Box>Filter {filter?.displayType} type not found</Box>

	}

	return (
		<Box sx={{ display: 'flex' }}>
			<Tabs
				orientation="vertical"
				variant="scrollable"
				visibleScrollbar={true}
				scrollButtons={false}
				value={selectedTab}
				onChange={(event, newValue) => setSelectedTab(newValue)}
				sx={{
					borderRight: 1,
					borderColor: 'divider',
					width: '220px',
					height: '216px',
					padding: '8px 8px 8px 12px'
				}}
				TabIndicatorProps={{
					style: { display: 'none' },
				}}
			>
				{filterOptions.map((filter, index) => {
					// For composite filters, check if any subfilter has values
					const isFilterApplied = filter?.displayType === 'compositeSelectFilter'
						? filter.subFilters?.some((subFilter) => {
							return newFilters[subFilter.key] && newFilters[subFilter.key].length > 0;
						})
						: newFilters[filter.key];

					return (
						<StyledFilterTab
							key={index}
							isFilterApplied={isFilterApplied}
							label={
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									width: '100%',
								}}
							>
								<Typography variant={selectedTab === index ? "fw500s12lh24c6D72F6" : "fw400s12lh24c3D4B5A"}> {
									t(`filters:${normalizeKey(filter.label)}`, filter.label)
								} </Typography>
								{selectedTab === index && (
									<ArrowForwardIosIcon
										sx={{
											fontSize: '12px',
											fontWeight: 500,
											color: '#6D72F6',
											marginLeft: 'auto',
										}}
									/>
								)}
							</Box>
						}
					/>
				);
				})}
			</Tabs>

			<Box sx={{ flexGrow: 1, padding: '8px 16px 8px 12px', height: '216px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
				<Box>
					{renderFilterSelectionView(filterOptions[selectedTab])}
				</Box>

				{shouldShowClearSelection() && <Box sx={{ textAlign: 'right' }}>
					<PrimaryTextButton onClick={() => clearTabFilterSelection()} sx={{ position: 'absolute', bottom: 60, right: 16 }}>
						{t('generic_table:filters.clear_selection')}
					</PrimaryTextButton>
				</Box>
				}
			</Box>
		</Box>
	);
};

export default FilterBody;
